import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { usePortal } from '../../../context/PortalContext';
import PortalPageShell, { PortalLoading } from '../PortalPageShell';
import ChildSelector from '../ChildSelector';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Button } from '../../ui/button';
import { CheckCircle, IndianRupee, AlertCircle, FileText } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { openFeeReceiptPortal } from '../../../utils/documentPortalUrl';
import { useToast } from '../../../hooks/use-toast';

function formatRupee(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

function FeeSummaryTile({ label, value, variant = 'neutral' }) {
  const styles = {
    neutral: 'border-border bg-card',
    paid: 'border-emerald-500/30 bg-emerald-500/10',
    pending: 'border-amber-500/50 bg-amber-500/10 ring-1 ring-amber-500/20',
    cleared: 'border-border bg-muted/30',
  };

  return (
    <Card className={cn('border', styles[variant])}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p
          className={cn(
            'font-semibold text-foreground',
            variant === 'pending' ? 'text-3xl font-bold text-amber-700 dark:text-amber-400' : 'text-2xl',
            variant === 'cleared' && 'text-muted-foreground',
          )}
        >
          {value}
        </p>
        {variant === 'pending' ? (
          <span className="inline-flex mt-2 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-800 dark:text-amber-200">
            Amount due
          </span>
        ) : null}
        {variant === 'cleared' ? (
          <span className="inline-flex items-center gap-1 mt-2 text-xs text-muted-foreground">
            <CheckCircle className="h-3.5 w-3.5" />
            Cleared
          </span>
        ) : null}
      </CardContent>
    </Card>
  );
}

function breakdownAmount(row) {
  const n = row.yearly_amount ?? row.amount ?? row.structure_amount;
  return formatRupee(n);
}

function FeeStructureContent({ calc }) {
  if (!calc) {
    return (
      <p className="text-muted-foreground text-sm py-4">
        Select a child or contact school for fee breakdown.
      </p>
    );
  }

  const pending = Number(calc.pending ?? 0);
  const pendingVariant = pending > 0 ? 'pending' : 'cleared';
  const allRows = calc.breakdown || [];
  const includedRows = allRows.filter((b) => b.included !== false);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FeeSummaryTile label="Yearly total" value={formatRupee(calc.yearly_total)} variant="neutral" />
        <FeeSummaryTile label="Paid" value={formatRupee(calc.paid)} variant="paid" />
        <div className="relative">
          {pending > 0 ? (
            <div className="absolute -top-1 -right-1 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-white shadow-sm">
              <IndianRupee className="h-4 w-4" />
            </div>
          ) : null}
          <FeeSummaryTile label="Pending" value={formatRupee(calc.pending)} variant={pendingVariant} />
        </div>
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            {pending > 0 ? (
              <AlertCircle className="h-4 w-4 text-amber-600" />
            ) : null}
            Fee breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          {allRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No fee line items configured.</p>
          ) : includedRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              Fee types exist for your class, but none apply to your profile (library/transport settings).
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {includedRows.map((b, i) => (
                <li key={b.fee_type_id ?? i} className="flex justify-between items-start gap-4 py-3 text-sm first:pt-0 last:pb-0">
                  <div>
                    <span className="text-foreground font-medium">{b.fee_type_name || b.label}</span>
                    {b.frequency && b.structure_amount != null ? (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatRupee(b.structure_amount)}
                        {' '}
                        {b.frequency === 'monthly' ? 'per month' : b.frequency}
                      </p>
                    ) : null}
                  </div>
                  <span className="text-foreground tabular-nums font-medium shrink-0">
                    {breakdownAmount(b)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function PortalStudentFees({ parentMode = false }) {
  const { user } = useAuth();
  const { activeStudentId } = usePortal();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('structure');
  const paymentIdParam = searchParams.get('payment_id');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (parentMode && user?.role_slug === 'parent') {
          const res = await api.get('/portal/parent/fees');
          if (!cancelled) setData({ parent: res.data?.data });
        } else {
          const res = await api.get('/portal/student/fees');
          if (!cancelled) setData({ student: res.data?.data });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [parentMode, user?.role_slug, activeStudentId]);

  const payments = parentMode
    ? data?.parent?.payments ?? []
    : data?.student?.payments ?? [];

  const openReceipt = useCallback(async (payment) => {
    if (!payment?.id && !payment?.portal_token) return;
    try {
      await openFeeReceiptPortal(payment, api);
    } catch (error) {
      toast({
        title: 'Could not open receipt',
        description: error.message || 'Receipt unavailable',
        variant: 'destructive',
      });
    }
  }, [toast]);

  useEffect(() => {
    if (loading || !paymentIdParam || parentMode) return;
    const payment = payments.find((p) => String(p.id) === String(paymentIdParam));
    if (!payment) return;
    setActiveTab('history');
    openReceipt(payment).finally(() => {
      const next = new URLSearchParams(searchParams);
      next.delete('payment_id');
      setSearchParams(next, { replace: true });
    });
  }, [loading, paymentIdParam, payments, parentMode, openReceipt, searchParams, setSearchParams]);

  const calc = parentMode ? null : data?.student?.calculation;

  return (
    <PortalPageShell
      title="Fees"
      description="Fee structure and payment history (read-only)"
      actions={parentMode ? <ChildSelector /> : null}
    >
      {loading ? <PortalLoading /> : null}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="structure">Structure</TabsTrigger>
          <TabsTrigger value="history">Payment history</TabsTrigger>
        </TabsList>
        <TabsContent value="structure" className="mt-4">
          <FeeStructureContent calc={calc} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base">Payment history</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-sm divide-y divide-border">
                {payments.map((p) => (
                  <li key={p.id} className="py-3 flex justify-between items-center gap-3 first:pt-0">
                    <div className="min-w-0">
                      <p className="text-muted-foreground">{p.payment_date || p.created_at}</p>
                      {p.receipt_no ? (
                        <p className="text-xs text-muted-foreground font-mono truncate">{p.receipt_no}</p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-medium text-foreground tabular-nums">
                        {formatRupee(p.total_amount ?? p.amount)}
                      </span>
                      {!parentMode && p.id ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => openReceipt(p)}
                          title="View receipt"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </li>
                ))}
                {payments.length === 0 ? (
                  <li className="py-6 text-center text-muted-foreground">No payments recorded.</li>
                ) : null}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PortalPageShell>
  );
}
